/**
 * Global error-interceptor chain for the blustreamApi hooks.
 *
 * Pattern: register N handlers at app boot (e.g. 401 -> redirect, 5xx -> toast,
 * network drop -> banner, logging). Hooks invoke `runErrorInterceptors(err, onError)`
 * after a failing request: the optional per-call `onError` runs first, then each
 * registered global handler runs in registration order. Any handler returning
 * the `SUPPRESS` sentinel halts the chain and tells the hook to skip setting
 * `error` state on the caller — the page has already handled the error
 * (modal, redirect, custom UI, etc.).
 *
 * apiFetch itself does NOT use this chain. It throws typed errors as before;
 * only the hooks layer dispatches through this module. Non-hook callers of
 * apiFetch see raw errors.
 */

/**
 * Sentinel returned by an interceptor to indicate the error has been handled.
 * Halts further interceptors AND tells the caller's hook not to surface the
 * error to UI state.
 */
export const SUPPRESS = Symbol('blustreamApi.SUPPRESS');

const handlers = [];

/**
 * Register a global error interceptor.
 *
 *   fn: (err) => SUPPRESS | undefined
 *     return SUPPRESS  -> chain halts, hook does not set `error` state
 *     return undefined -> next interceptor runs, then `error` surfaces normally
 *
 * Handlers run in registration order. Returns an unsubscribe fn.
 *
 * @param {(err: Error) => symbol | undefined} fn
 * @returns {() => void} unsubscribe
 */
export function addErrorInterceptor(fn) {
  if (typeof fn !== 'function') {
    throw new TypeError('addErrorInterceptor expects a function');
  }
  handlers.push(fn);
  return function unsubscribe() {
    const idx = handlers.indexOf(fn);
    if (idx !== -1) handlers.splice(idx, 1);
  };
}

/**
 * Run the per-call onError (if supplied) followed by the global chain.
 * Returns true if any handler returned SUPPRESS, false otherwise.
 *
 * Used internally by the hooks; not exported through the barrel.
 *
 * @param {Error} err
 * @param {(err: Error) => symbol | undefined} [onError]
 * @returns {boolean} suppressed
 */
export function runErrorInterceptors(err, onError) {
  if (typeof onError === 'function') {
    if (onError(err) === SUPPRESS) return true;
  }
  for (const fn of handlers.slice()) {
    if (fn(err) === SUPPRESS) return true;
  }
  return false;
}

/**
 * Test-only: clear all registered interceptors. Not exported through the
 * barrel; tests import directly from './errorInterceptors'.
 */
export function _clearInterceptorsForTests() {
  handlers.length = 0;
}
